import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Table, Form } from 'antd';
import moment from 'moment';

import { getTransactions } from 'features/statement/actions';

import { RejectedMessage, TableWrapper } from 'componets/ui';
import { TransactionsFilters } from './TransactionsFilters';

import {
	useAppDispatch,
	useAppSelector,
	useDebouncedChange,
	useFiltersData,
	useTranslate,
} from 'hooks';

import { ColumnTypes, IFilterValues } from 'models';
import { RequestStatus, PAGE_SIZE } from 'utils/constants';
import { selectUser } from 'selectors';

import { columns } from './columns';

import s from '../index.module.scss';
import '../index.scss';

export const Transactions: FC<{ type: string }> = ({ type }) => {
	const { loadMoreText } = useTranslate(['loadMoreText']);

	const [form] = Form.useForm();

	const watchedValues = Form.useWatch([], form);

	const [offset, setOffset] = useState(0);

	useEffect(() => {
		setOffset(0);
	}, [watchedValues]);

	const { values } = useDebouncedChange(watchedValues);
	const { filtersData } = useFiltersData(values);

	const { transactions, status: requestStatus } = useAppSelector((state) => state.statement);
	const user = useAppSelector(selectUser);

	const startDate = moment(user.registrationDate).isAfter(moment().startOf('year'))
		? moment(user.registrationDate)
		: moment().startOf('year');

	const [isLoadMoreButton, setIsLoadMoreButton] = useState(true);
	const dispatch = useAppDispatch();

	const params = useMemo(
		() => ({
			startDate: filtersData.minDateOfPurchase,
			endDate: filtersData.maxDateOfPurchase,
			offset,
			pageSize: PAGE_SIZE,
			...filtersData,
		}),
		[filtersData, offset],
	);

	const onGetTransactions = useCallback(
		() =>
			dispatch(getTransactions({ params }))
				.unwrap()
				.then((d: any) => {
					const isMoreLoad = d?.length >= PAGE_SIZE;

					setIsLoadMoreButton(isMoreLoad);
				}),
		[dispatch, params],
	);

	useEffect(() => {
		if (type === 'transactions') {
			onGetTransactions().then();
		}
	}, [type, onGetTransactions]);

	if (requestStatus === RequestStatus.REJECTED) {
		return <RejectedMessage />;
	}

	return (
		<div>
			<TransactionsFilters
				form={form}
				initialValues={
					{
						minDateOfPurchase: startDate.format('YYYY-MM-DD'),
						maxDateOfPurchase: moment().format('YYYY-MM-DD'),
					} as IFilterValues
				}
				values={values}
			/>

			<TableWrapper
				data={transactions}
				loading={requestStatus === RequestStatus.PENDING && transactions.length === 0}
			>
				<Table
					loading={requestStatus === RequestStatus.PENDING}
					pagination={false}
					scroll={{ x: 650 }}
					className="statementTable statementTable--reports"
					columns={columns as ColumnTypes}
					dataSource={transactions}
					footer={() => {
						return isLoadMoreButton ? (
							<div className={s.loadMore}>
								<Button type="primary" onClick={() => setOffset(offset + PAGE_SIZE)}>
									{loadMoreText}
								</Button>
							</div>
						) : (
							''
						);
					}}
				/>
			</TableWrapper>
		</div>
	);
};
